import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "permanina_session_token";

// In-memory fallback when localStorage/sessionStorage are restricted (Safari ITP, private mode, etc.)
let _memoryToken = null;

function _safeGet(storage) {
  try { return storage?.getItem(TOKEN_KEY); } catch (_) { return null; }
}
function _safeSet(storage, value) {
  try {
    if (value) storage?.setItem(TOKEN_KEY, value);
    else storage?.removeItem(TOKEN_KEY);
    return true;
  } catch (_) { return false; }
}

export const api = axios.create({
  baseURL: API,
});

// Apply the Authorization default header on the SHARED axios instance so it is
// always sent (works even if request interceptors get reordered / dropped by bundlers).
function applyAuthHeader(token) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else if (api.defaults.headers.common && "Authorization" in api.defaults.headers.common) {
    delete api.defaults.headers.common.Authorization;
  }
}

export const getStoredToken = () => {
  if (_memoryToken) return _memoryToken;
  if (typeof window === "undefined") return null;
  const fromLocal = _safeGet(window.localStorage);
  if (fromLocal) {
    _memoryToken = fromLocal;
    applyAuthHeader(fromLocal);
    return fromLocal;
  }
  const fromSession = _safeGet(window.sessionStorage);
  if (fromSession) {
    _memoryToken = fromSession;
    applyAuthHeader(fromSession);
    return fromSession;
  }
  return null;
};

export const setStoredToken = (token) => {
  _memoryToken = token || null;
  applyAuthHeader(token);
  if (typeof window === "undefined") return;
  const okLocal = _safeSet(window.localStorage, token);
  if (!okLocal) _safeSet(window.sessionStorage, token);
};

export const clearStoredToken = () => {
  _memoryToken = null;
  applyAuthHeader(null);
  if (typeof window === "undefined") return;
  _safeSet(window.localStorage, null);
  _safeSet(window.sessionStorage, null);
};

// Initialize header eagerly at module load (synchronous read from localStorage).
// This guarantees the very first request (e.g. AuthContext's /auth/me on mount)
// already has Authorization, without depending on interceptor timing.
(function initialiseAuthHeader() {
  if (typeof window === "undefined") return;
  const t = _safeGet(window.localStorage) || _safeGet(window.sessionStorage);
  if (t) {
    _memoryToken = t;
    applyAuthHeader(t);
  }
})();

// Defensive: also attach per-request through an interceptor, as a belt-and-suspenders.
api.interceptors.request.use((config) => {
  const token = _memoryToken || getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Stale token cleanup: only fire on /auth/me 401 (avoids wiping token on unrelated 401s).
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const url = error?.config?.url || "";
    if (error?.response?.status === 401 && url.endsWith("/auth/me") && _memoryToken) {
      clearStoredToken();
    }
    return Promise.reject(error);
  }
);
